import {
    commonReducer,
    initialCommonState,
    type commonAction,
    type commonState,
} from "@/hooks/reducers/common.reducer";
import type { parsedApiError } from "@/types/api/errors.api";
import type { simulationSession } from "../simulationConfiguration/types.api";
import type { answerManagementData } from "./types.api";

export interface answersState extends commonState {
    simulationData: {
        sessions: simulationSession[];
        isClosed: boolean;
    };
    answers: Record<string, string>;
    questions: answerManagementData[];
    count: number;
    filters: {
        sessionId: number | null;
        testId: number | null;
    };
    currentPage: number;
    isSaving: boolean;
}

export type answersAction =
    | commonAction
    | {
        type: "SET_SIMULATION_DATA";
        payload: { sessions: simulationSession[]; state: string };
    }
    | {
        type: "SET_PAGINATED_QUESTIONS";
        payload: { results: answerManagementData[]; count: number };
    }
    | {
        type: "UPDATE_ONE_ANSWER";
        payload: { questionId: number; answer: string };
    }
    | {
        type: "SET_FILTERS";
        payload: { sessionId?: number | null; testId?: number | null };
    }
    | { type: "SET_PAGE"; payload: number }
    | { type: "SAVE_START" }
    | { type: "SAVE_SUCCESS" }
    | { type: "SAVE_FAILURE"; payload: parsedApiError };

export const createInitialState = (): answersState => ({
    ...initialCommonState,
    isLoading: true,
    simulationData: { sessions: [], isClosed: false },
    answers: {},
    questions: [],
    count: 0,
    filters: { sessionId: null, testId: null },
    currentPage: 1,
    isSaving: false,
});

export const answersReducer = (
    state: answersState,
    action: answersAction,
): answersState => {
    const commonState = commonReducer(state, action as commonAction);
    if (commonState !== state) return { ...state, ...commonState };

    switch (action.type) {
        case "SET_SIMULATION_DATA": {
            const { sessions, state: simState } = action.payload;
            return {
                ...state,
                simulationData: {
                    sessions: sessions || [],
                    isClosed: simState === "closed",
                },
            };
        }
        case "SET_PAGINATED_QUESTIONS": {
            const { results, count } = action.payload;
            const newAnswers = { ...state.answers };
            let answersChanged = false;
            results.forEach((q) => {
                const questionIdStr = String(q.id);
                const newAnswer = q.student_answer || "";
                if (newAnswers[questionIdStr] !== newAnswer) {
                    newAnswers[questionIdStr] = newAnswer;
                    answersChanged = true;
                }
            });

            return {
                ...state,
                questions: results,
                count,
                answers: answersChanged ? newAnswers : state.answers,
                isLoading: false,
            };
        }
        case "UPDATE_ONE_ANSWER":
            return {
                ...state,
                answers: {
                    ...state.answers,
                    [String(action.payload.questionId)]: action.payload.answer,
                },
            };
        case "SET_FILTERS":
            return {
                ...state,
                filters: { ...state.filters, ...action.payload },
                currentPage: 1,
                questions: [],
                count: 0,
            };
        case "SET_PAGE":
            return { ...state, currentPage: action.payload };
        case "SAVE_START":
            return { ...state, isSaving: true, error: null };
        case "SAVE_SUCCESS":
            return { ...state, isSaving: false };
        case "SAVE_FAILURE":
            return { ...state, isSaving: false, error: action.payload };
        default:
            return state;
    }
};
